import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { UserPlus, X, Shield, UserX, UserCheck, Pencil, Users, KeyRound } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

const EMPTY_FORM = { email: "", first_name: "", last_name: "", role_names: [], password: "", confirmPassword: "" };
const EMPTY_EDIT_FORM = { email: "", first_name: "", last_name: "", role_names: [] };
const EMPTY_RESET_FORM = { password: "", confirmPassword: "", must_reset_password: true };

// Per-role color so the roster reads at a glance (same convention as
// Alerts.jsx's SEVERITY_META) instead of every chip looking identical
// except super_admin. Falls back to a neutral chip for any role not
// listed here, so a newly-created custom role never renders broken.
const ROLE_META = {
  super_admin: { color: "#a16207", bg: "rgba(234,179,8,0.1)", border: "rgba(234,179,8,0.25)" },
  admin: { color: "#2563eb", bg: "rgba(37,99,235,0.08)", border: "rgba(37,99,235,0.2)" },
  dispatcher: { color: "#7c3aed", bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.2)" },
};
const DEFAULT_ROLE_META = { color: "var(--ink-2)", bg: "var(--bg)", border: "var(--line)" };

function initials(sipName, email) {
  const src = (sipName || "").trim() || email || "";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// Roster comes from /users/roster -- unlike /impersonation/users (active-only,
// excludes the caller, capped at 25, built for the impersonate-user picker)
// this includes inactive users and the caller's own row, since an admin
// needs to see who's deactivated to reactivate them later.
export default function TeamMembers() {
  const [users, setUsers] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [statusNote, setStatusNote] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [createdCred, setCreatedCred] = useState(null); // { email, password, wasGenerated }

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  const [resettingId, setResettingId] = useState(null);
  const [resetForm, setResetForm] = useState(EMPTY_RESET_FORM);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetCred, setResetCred] = useState(null); // { email, password, wasGenerated, mustResetPassword }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${apiBaseUrl}/users/roster`, authHeaders()),
      axios.get(`${apiBaseUrl}/users/assignable-roles`, authHeaders()),
    ])
      .then(([usersRes, rolesRes]) => {
        setUsers(usersRes.data.records || []);
        setAssignableRoles(rolesRes.data.records || []);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const active = users.filter((u) => u.active).length;
    const elevated = users.filter((u) => (u.roles || []).some((r) => r === "admin" || r === "super_admin")).length;
    return { total: users.length, active, inactive: users.length - active, elevated };
  }, [users]);

  function setActive(user, active) {
    if (active === false && !window.confirm(
      `Deactivate ${user.sip_name || user.email}? They'll be signed out and won't be able to log back in until you reactivate them.`
    )) {
      return;
    }
    setStatusError(null);
    setStatusBusyId(user.id);
    axios
      .post(`${apiBaseUrl}/users/${user.id}/${active ? "reactivate" : "deactivate"}`, {}, authHeaders())
      .then((res) => {
        const n = res.data?.unassigned_trucks || 0;
        if (n > 0) {
          setStatusNote(`${n} truck${n === 1 ? "" : "s"} previously assigned to ${user.sip_name || user.email} ${n === 1 ? "is" : "are"} now Unassigned.`);
        }
        load();
      })
      .catch((err) => setStatusError(err.response?.data?.detail || err.message))
      .finally(() => setStatusBusyId(null));
  }

  function toggleRole(name) {
    setForm((f) => ({
      ...f,
      role_names: f.role_names.includes(name)
        ? f.role_names.filter((r) => r !== name)
        : [...f.role_names, name],
    }));
  }

  function toggleEditRole(name) {
    setEditForm((f) => ({
      ...f,
      role_names: f.role_names.includes(name)
        ? f.role_names.filter((r) => r !== name)
        : [...f.role_names, name],
    }));
  }

  function startEdit(user) {
    setEditingId(user.id);
    setEditError(null);
    setEditForm({
      email: user.email || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      role_names: user.roles || [],
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(EMPTY_EDIT_FORM);
    setEditError(null);
  }

  function saveEdit(user) {
    setEditError(null);
    if (!editForm.email.trim() || !editForm.first_name.trim() || !editForm.last_name.trim()) {
      setEditError("Email, first name, and last name are all required.");
      return;
    }
    if (!editForm.role_names.length) {
      setEditError("Pick at least one role.");
      return;
    }
    setEditSubmitting(true);
    const profileChanged =
      editForm.email !== user.email || editForm.first_name !== (user.first_name || "") ||
      editForm.last_name !== (user.last_name || "");
    const rolesChanged =
      editForm.role_names.length !== (user.roles || []).length ||
      editForm.role_names.some((r) => !(user.roles || []).includes(r));

    const calls = [];
    if (profileChanged) {
      calls.push(axios.put(`${apiBaseUrl}/users/${user.id}/profile`, {
        email: editForm.email, first_name: editForm.first_name, last_name: editForm.last_name,
      }, authHeaders()));
    }
    if (rolesChanged) {
      calls.push(axios.put(`${apiBaseUrl}/users/${user.id}/roles`, {
        role_names: editForm.role_names,
      }, authHeaders()));
    }
    Promise.all(calls)
      .then(() => {
        cancelEdit();
        load();
      })
      .catch((err) => setEditError(err.response?.data?.detail || err.message))
      .finally(() => setEditSubmitting(false));
  }

  function startReset(user) {
    setResettingId(user.id);
    setResetError(null);
    setResetForm(EMPTY_RESET_FORM);
  }

  function cancelReset() {
    setResettingId(null);
    setResetForm(EMPTY_RESET_FORM);
    setResetError(null);
  }

  function saveReset(user) {
    setResetError(null);
    const password = resetForm.password.trim();
    if (password) {
      if (password.length < 8) {
        setResetError("Password must be at least 8 characters.");
        return;
      }
      if (password !== resetForm.confirmPassword.trim()) {
        setResetError("Passwords don't match.");
        return;
      }
    }
    setResetSubmitting(true);
    axios
      .put(`${apiBaseUrl}/users/${user.id}/password`, {
        password: password || undefined,
        must_reset_password: resetForm.must_reset_password,
      }, authHeaders())
      .then((res) => {
        setResetCred({
          email: res.data.email,
          password: res.data.temporary_password,
          wasGenerated: res.data.password_was_generated,
          mustResetPassword: res.data.must_reset_password,
        });
        cancelReset();
      })
      .catch((err) => setResetError(err.response?.data?.detail || err.message))
      .finally(() => setResetSubmitting(false));
  }

  function submit() {
    setFormError(null);
    if (!form.email.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      setFormError("Email, first name, and last name are all required.");
      return;
    }
    if (!form.role_names.length) {
      setFormError("Pick at least one role.");
      return;
    }
    const password = form.password.trim();
    if (password) {
      if (password.length < 8) {
        setFormError("Password must be at least 8 characters.");
        return;
      }
      if (password !== form.confirmPassword.trim()) {
        setFormError("Passwords don't match.");
        return;
      }
    }
    setSubmitting(true);
    const payload = {
      email: form.email, first_name: form.first_name, last_name: form.last_name,
      role_names: form.role_names, password: password || undefined,
    };
    axios
      .post(`${apiBaseUrl}/users/create-with-roles`, payload, authHeaders())
      .then((res) => {
        setCreatedCred({
          email: res.data.email,
          password: res.data.temporary_password,
          wasGenerated: res.data.password_was_generated,
        });
        setForm(EMPTY_FORM);
        setShowForm(false);
        load();
      })
      .catch((err) => setFormError(err.response?.data?.detail || err.message))
      .finally(() => setSubmitting(false));
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Members</h1>
          <div style={styles.subtitle}>
            Everyone with access to this fleet, and what they can do. Fleet admins can add new users, assign any
            role except super_admin, and deactivate someone to immediately revoke their access.
          </div>
        </div>
        <button
          style={styles.addBtn}
          onClick={() => {
            setShowForm((s) => !s);
            setFormError(null);
          }}
        >
          {showForm ? <X size={14} /> : <UserPlus size={14} />} {showForm ? "Cancel" : "Add user"}
        </button>
      </div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, color: "var(--brand)", background: "rgba(37,99,235,0.08)" }}>
            <Users size={16} />
          </div>
          <div>
            <div style={styles.summaryValue}>{stats.total}</div>
            <div style={styles.summaryLabel}>Team members</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, color: "#16a34a", background: "rgba(22,163,74,0.08)" }}>
            <UserCheck size={16} />
          </div>
          <div>
            <div style={styles.summaryValue}>{stats.active}</div>
            <div style={styles.summaryLabel}>Active</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, color: "#dc2626", background: "rgba(220,38,38,0.08)" }}>
            <UserX size={16} />
          </div>
          <div>
            <div style={styles.summaryValue}>{stats.inactive}</div>
            <div style={styles.summaryLabel}>Deactivated</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, color: "#a16207", background: "rgba(234,179,8,0.1)" }}>
            <Shield size={16} />
          </div>
          <div>
            <div style={styles.summaryValue}>{stats.elevated}</div>
            <div style={styles.summaryLabel}>Admins</div>
          </div>
        </div>
      </div>

      {createdCred && (
        <div style={styles.credBox}>
          <div style={styles.credTitle}>
            {createdCred.wasGenerated
              ? "User created -- save this password now, it won't be shown again"
              : "User created with the password you set"}
          </div>
          <div style={styles.credRow}>
            <span style={styles.credLabel}>Email</span>
            <code style={styles.credValue}>{createdCred.email}</code>
          </div>
          <div style={styles.credRow}>
            <span style={styles.credLabel}>Password</span>
            <code style={styles.credValue}>{createdCred.password}</code>
          </div>
          <button style={styles.credDismiss} onClick={() => setCreatedCred(null)}>
            Dismiss
          </button>
        </div>
      )}

      {resetCred && (
        <div style={styles.credBox}>
          <div style={styles.credTitle}>
            {resetCred.wasGenerated
              ? "Password reset -- save this now, it won't be shown again"
              : "Password reset to the value you set"}
          </div>
          <div style={styles.credRow}>
            <span style={styles.credLabel}>Email</span>
            <code style={styles.credValue}>{resetCred.email}</code>
          </div>
          <div style={styles.credRow}>
            <span style={styles.credLabel}>Password</span>
            <code style={styles.credValue}>{resetCred.password}</code>
          </div>
          {resetCred.mustResetPassword && (
            <div style={styles.credNote}>
              This user will be required to set their own new password the next time they log in.
            </div>
          )}
          <button style={styles.credDismiss} onClick={() => setResetCred(null)}>
            Dismiss
          </button>
        </div>
      )}

      {showForm && (
        <div style={styles.formCard}>
          {formError && <div style={styles.errorBox}>{formError}</div>}
          <div style={styles.formRow}>
            <label style={styles.formLabel}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              style={styles.formInput}
              placeholder="name@example.com"
              disabled={submitting}
            />
          </div>
          <div style={styles.formRowPair}>
            <div style={{ flex: 1 }}>
              <label style={styles.formLabel}>First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                style={styles.formInput}
                disabled={submitting}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.formLabel}>Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                style={styles.formInput}
                disabled={submitting}
              />
            </div>
          </div>
          <div style={styles.formRowPair}>
            <div style={{ flex: 1 }}>
              <label style={styles.formLabel}>Password (optional)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                style={styles.formInput}
                placeholder="Leave blank to auto-generate"
                disabled={submitting}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.formLabel}>Confirm password</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                style={styles.formInput}
                disabled={submitting || !form.password}
              />
            </div>
          </div>
          <div style={styles.formRow}>
            <label style={styles.formLabel}>Roles</label>
            <div style={styles.roleOptionRow}>
              {assignableRoles.map((r) => {
                const active = form.role_names.includes(r.name);
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => toggleRole(r.name)}
                    disabled={submitting}
                    style={{ ...styles.roleOption, ...(active ? styles.roleOptionActive : {}) }}
                    title={r.description || ""}
                  >
                    {r.name}
                  </button>
                );
              })}
              {!assignableRoles.length && <span style={styles.noRoles}>No assignable roles found.</span>}
            </div>
          </div>
          <button style={styles.submitBtn} onClick={submit} disabled={submitting}>
            {submitting ? "Creating…" : "Create user"}
          </button>
        </div>
      )}

      {error && <div style={styles.errorBox}>Couldn't load team members: {error}</div>}
      {statusError && <div style={styles.errorBox}>{statusError}</div>}
      {statusNote && (
        <div style={styles.noteBox}>
          {statusNote}
          <button style={styles.credDismiss} onClick={() => setStatusNote(null)}>Dismiss</button>
        </div>
      )}

      <div style={styles.sectionLabel}>Team roster</div>
      <div style={styles.list}>
        {users.map((u) =>
          editingId === u.id ? (
            <div key={u.id} style={styles.editCard}>
              {editError && <div style={styles.errorBox}>{editError}</div>}
              <div style={styles.formRow}>
                <label style={styles.formLabel}>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  style={styles.formInput}
                  disabled={editSubmitting}
                />
              </div>
              <div style={styles.formRowPair}>
                <div style={{ flex: 1 }}>
                  <label style={styles.formLabel}>First name</label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                    style={styles.formInput}
                    disabled={editSubmitting}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.formLabel}>Last name</label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                    style={styles.formInput}
                    disabled={editSubmitting}
                  />
                </div>
              </div>
              <div style={styles.formRow}>
                <label style={styles.formLabel}>Roles</label>
                <div style={styles.roleOptionRow}>
                  {assignableRoles.map((r) => {
                    const active = editForm.role_names.includes(r.name);
                    return (
                      <button
                        type="button"
                        key={r.id}
                        onClick={() => toggleEditRole(r.name)}
                        disabled={editSubmitting}
                        style={{ ...styles.roleOption, ...(active ? styles.roleOptionActive : {}) }}
                        title={r.description || ""}
                      >
                        {r.name}
                      </button>
                    );
                  })}
                  {u.roles?.includes("super_admin") && !editForm.role_names.includes("super_admin") && (
                    <span style={styles.roleChipSuper}>super_admin (not editable here)</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={styles.submitBtn} onClick={() => saveEdit(u)} disabled={editSubmitting}>
                  {editSubmitting ? "Saving…" : "Save changes"}
                </button>
                <button style={styles.cancelEditBtn} onClick={cancelEdit} disabled={editSubmitting}>
                  Cancel
                </button>
              </div>
            </div>
          ) : resettingId === u.id ? (
            <div key={u.id} style={styles.editCard}>
              {resetError && <div style={styles.errorBox}>{resetError}</div>}
              <div style={styles.formRowPair}>
                <div style={{ flex: 1 }}>
                  <label style={styles.formLabel}>New password (optional)</label>
                  <input
                    type="password"
                    value={resetForm.password}
                    onChange={(e) => setResetForm((f) => ({ ...f, password: e.target.value }))}
                    style={styles.formInput}
                    placeholder="Leave blank to auto-generate"
                    disabled={resetSubmitting}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.formLabel}>Confirm password</label>
                  <input
                    type="password"
                    value={resetForm.confirmPassword}
                    onChange={(e) => setResetForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                    style={styles.formInput}
                    disabled={resetSubmitting || !resetForm.password}
                  />
                </div>
              </div>
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={resetForm.must_reset_password}
                  onChange={(e) => setResetForm((f) => ({ ...f, must_reset_password: e.target.checked }))}
                  disabled={resetSubmitting}
                />
                Password needs reset -- require {u.sip_name || u.email} to set their own new password next time they log in
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={styles.submitBtn} onClick={() => saveReset(u)} disabled={resetSubmitting}>
                  {resetSubmitting ? "Saving…" : "Reset password"}
                </button>
                <button style={styles.cancelEditBtn} onClick={cancelReset} disabled={resetSubmitting}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div key={u.id} style={{ ...styles.userRow, ...(u.active ? {} : styles.userRowInactive) }}>
              <div style={styles.userIdentity}>
                <div style={{ ...styles.avatar, ...(u.active ? {} : styles.avatarInactive) }}>
                  {initials(u.sip_name, u.email)}
                </div>
                <div>
                  <div style={styles.userName}>
                    {u.sip_name || u.email}
                    {u.is_self && <span style={styles.youTag}>you</span>}
                    {!u.active && <span style={styles.inactiveTag}>Deactivated</span>}
                  </div>
                  <div style={styles.userEmail}>{u.email}</div>
                </div>
              </div>
              <div style={styles.rowRight}>
                <div style={styles.roleChips}>
                  {(u.roles || []).map((r) => {
                    const meta = ROLE_META[r] || DEFAULT_ROLE_META;
                    return (
                      <span key={r} style={{ ...styles.roleChip, color: meta.color, background: meta.bg, borderColor: meta.border }}>
                        {r === "super_admin" && <Shield size={10} style={{ marginRight: 3 }} />}
                        {r}
                      </span>
                    );
                  })}
                  {!u.roles?.length && <span style={styles.noRoles}>No roles</span>}
                </div>
                {u.can_manage && (
                  <>
                    <button style={styles.editBtn} onClick={() => startEdit(u)} title="Edit email, name, or roles">
                      <Pencil size={13} /> Edit
                    </button>
                    <button style={styles.editBtn} onClick={() => startReset(u)} title="Set a new password for this user">
                      <KeyRound size={13} /> Reset password
                    </button>
                    {u.active ? (
                      <button
                        style={styles.deactivateBtn}
                        disabled={statusBusyId === u.id}
                        onClick={() => setActive(u, false)}
                        title="Revoke this person's access"
                      >
                        <UserX size={13} /> {statusBusyId === u.id ? "Working…" : "Deactivate"}
                      </button>
                    ) : (
                      <button
                        style={styles.reactivateBtn}
                        disabled={statusBusyId === u.id}
                        onClick={() => setActive(u, true)}
                        title="Restore this person's access"
                      >
                        <UserCheck size={13} /> {statusBusyId === u.id ? "Working…" : "Reactivate"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        )}
        {!loading && !error && users.length === 0 && (
          <div style={styles.emptyState}>No other team members yet -- use "Add user" above to create one.</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 620, lineHeight: 1.5 },
  addBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#fff",
    background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 16px", cursor: "pointer",
    flexShrink: 0,
  },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  noteBox: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)", color: "var(--ink)",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20, maxWidth: 760 },
  summaryCard: {
    display: "flex", alignItems: "center", gap: 12, background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-lg)", padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  summaryIcon: {
    width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  sectionLabel: {
    fontSize: 11.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5,
    marginBottom: 8, maxWidth: 760,
  },

  credBox: {
    background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: "var(--radius-lg)",
    padding: "16px 18px", marginBottom: 20, maxWidth: 620,
  },
  credTitle: { fontSize: 13, fontWeight: 700, color: "#16a34a", marginBottom: 10 },
  credRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 },
  credLabel: { fontSize: 12, color: "var(--muted)", width: 70, flexShrink: 0 },
  credValue: {
    fontSize: 13, fontWeight: 700, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 6, padding: "3px 8px", fontFamily: "monospace",
  },
  credDismiss: {
    fontSize: 12, fontWeight: 600, color: "var(--muted)", background: "none", border: "none", cursor: "pointer",
    marginTop: 8, padding: 0, textDecoration: "underline",
  },
  credNote: { fontSize: 12, color: "var(--muted)", marginTop: 8, lineHeight: 1.4 },

  checkboxRow: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)",
    cursor: "pointer",
  },

  formCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", padding: "18px 20px", marginBottom: 20, maxWidth: 620,
    display: "flex", flexDirection: "column", gap: 14,
  },
  formRow: { display: "flex", flexDirection: "column", gap: 6 },
  formRowPair: { display: "flex", gap: 14 },
  formLabel: { fontSize: 12, fontWeight: 700, color: "var(--ink-2)" },
  formInput: {
    fontSize: 13.5, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "8px 10px", width: "100%",
  },
  roleOptionRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  roleOption: {
    fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 999, padding: "6px 14px", cursor: "pointer",
  },
  roleOptionActive: { color: "#fff", background: "var(--brand)", border: "1px solid var(--brand)" },
  submitBtn: {
    fontSize: 13, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none", borderRadius: 8,
    padding: "9px 18px", cursor: "pointer", alignSelf: "flex-start",
  },

  list: { display: "flex", flexDirection: "column", gap: 8, maxWidth: 760 },
  userRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
    padding: "14px 16px", boxShadow: "var(--shadow-xs)",
  },
  userRowInactive: { opacity: 0.6 },
  userIdentity: { display: "flex", alignItems: "center", gap: 12 },
  avatar: {
    width: 36, height: 36, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: 12.5, fontWeight: 800, color: "#fff", background: "var(--brand)",
  },
  avatarInactive: { background: "var(--muted)" },
  userName: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 },
  userEmail: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  youTag: {
    fontSize: 10, fontWeight: 700, color: "var(--muted)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 999, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.03em",
  },
  inactiveTag: {
    fontSize: 10, fontWeight: 700, color: "#dc2626", background: "rgba(220,38,38,0.08)",
    border: "1px solid rgba(220,38,38,0.25)", borderRadius: 999, padding: "2px 8px",
    textTransform: "uppercase", letterSpacing: "0.03em",
  },
  rowRight: { display: "flex", alignItems: "center", gap: 12 },
  editBtn: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "var(--ink-2)",
    background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8,
    padding: "6px 11px", cursor: "pointer", flexShrink: 0,
  },
  editCard: {
    background: "var(--card)", border: "1px solid var(--brand)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12,
  },
  cancelEditBtn: {
    fontSize: 13, fontWeight: 700, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "9px 18px", cursor: "pointer", alignSelf: "flex-start",
  },
  deactivateBtn: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "#dc2626",
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 8,
    padding: "6px 11px", cursor: "pointer", flexShrink: 0,
  },
  reactivateBtn: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "#16a34a",
    background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 8,
    padding: "6px 11px", cursor: "pointer", flexShrink: 0,
  },
  roleChips: { display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" },
  roleChip: {
    display: "flex", alignItems: "center", fontSize: 11, fontWeight: 700, color: "var(--ink-2)",
    background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 999, padding: "3px 10px",
  },
  roleChipSuper: { color: "#a16207", background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.25)" },
  noRoles: { fontSize: 11.5, color: "var(--muted)", fontStyle: "italic" },

  emptyState: {
    textAlign: "center", color: "var(--muted)", fontSize: 13, padding: 40,
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
  },
};
